import { Link } from '@tanstack/react-router';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  type PaginationState,
  useReactTable,
} from '@tanstack/react-table';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';

import {
  type CardExpiringResponse,
  useGetExpiringCards,
} from '~entities/studios/api/get-expiring-cards';

import { DataTablePagination } from '~shared/components/table/data-table-pagination';
import { getStoredPageSize } from '~shared/helpers/page-size-preference';
import { Avatar, AvatarFallback } from '~shared/shadcn/ui/avatar';
import { Badge } from '~shared/shadcn/ui/badge';
import { Label } from '~shared/shadcn/ui/label';
import { Switch } from '~shared/shadcn/ui/switch';

export const CardExpiringTab = () => {
  const [activeOnly, setActiveOnly] = useState(true);
  const { data: expiringCards, isLoading } = useGetExpiringCards({
    activeOnly,
  });
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: getStoredPageSize(),
  });

  const columnHelper = createColumnHelper<CardExpiringResponse>();
  const table = useReactTable({
    data: expiringCards ?? [],
    columns: [columnHelper.display({ id: 'cards' })],
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    state: {
      pagination,
    },
  });

  return (
    <div>
      <div className='mb-4 flex items-center justify-end gap-2'>
        <Label htmlFor='active-parents-only' className='text-sm font-medium'>
          Active parents only
        </Label>
        <Switch
          id='active-parents-only'
          checked={activeOnly}
          onCheckedChange={(checked) => {
            setActiveOnly(checked);
            setPagination((prev) => ({ ...prev, pageIndex: 0 }));
          }}
        />
      </div>
      {isLoading ? (
        <div className='flex h-32 items-center justify-center'>
          <Loader2 className='size-6 animate-spin' />
        </div>
      ) : (
        <div className='space-y-4'>
          {expiringCards && expiringCards.length > 0 ? (
            <>
              <div className='space-y-2'>
                {table.getRowModel().rows.map((row) => {
                  const card = row.original;

                  return (
                    <div
                      key={card.id}
                      className='flex min-w-0 flex-col gap-3 rounded-xl border border-border/50 p-3.5 transition-all duration-150 hover:-translate-y-px hover:bg-muted/50 hover:shadow-md md:flex-row md:items-center md:justify-between'
                    >
                      <div className='flex min-w-0 shrink-0 items-center gap-2 md:max-w-[40%]'>
                        <Avatar className='shrink-0'>
                          <AvatarFallback className='bg-primary font-semibold text-primary-foreground'>
                            {card.parent.firstName.charAt(0)}
                            {card.parent.lastName.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div className='min-w-0'>
                          <Link to={`/family`} search={{ parentId: card.parent.id }}>
                            <p className='truncate text-sm font-semibold text-secondary hover:underline'>
                              {card.parent.firstName} {card.parent.lastName}
                            </p>
                          </Link>
                          <p className='truncate text-sm text-muted-foreground'>
                            {card.parent.email}
                          </p>
                        </div>
                      </div>
                      <div className='flex min-w-0 flex-wrap items-center gap-2 md:flex-1 md:justify-end'>
                        <div className='flex items-center gap-2'>
                          {card.isExpired ? (
                            <Badge
                              variant='outline'
                              className='border-red-300 bg-red-100 text-red-700'
                            >
                              Expired
                            </Badge>
                          ) : (
                            <Badge
                              variant='outline'
                              className='border-orange-300 bg-orange-100 text-xs text-orange-700'
                            >
                              Expiring in {card.daysUntilExpiration} days
                            </Badge>
                          )}
                        </div>
                        {card.isDefault && (
                          <Badge
                            variant='outline'
                            className='border-secondary/40 bg-secondary/10 text-secondary'
                          >
                            Default
                          </Badge>
                        )}
                        <div className='flex items-center gap-1.5'>
                          <p className='text-sm font-medium capitalize'>
                            {card.brand}
                          </p>
                          <p className='text-sm font-medium'>{card.last4}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <DataTablePagination table={table} />
            </>
          ) : (
            <div className='flex h-32 items-center justify-center'>
              <p className='text-sm text-muted-foreground'>
                {activeOnly
                  ? 'No expiring cards found for active parents'
                  : 'No expiring cards found'}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
